import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Zap, CalendarPlus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth';
import { QuickAdviceDialog } from '@/features/matters/components';
import { GlobalSearchDialog } from './GlobalSearchDialog';

interface QuickActionsBarProps {
  /** Show the global Search action (cases / contracts / tasks). MD + Partner only. */
  showSearch?: boolean;
}

/**
 * Case 360° (Stage 12) — dashboard quick actions: New matter, Quick Advice,
 * Add event, and (MD/Partner only) a global Search. Pure navigation/dialog
 * shortcuts over existing flows.
 */
export function QuickActionsBar({ showSearch = false }: QuickActionsBarProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [quickOpen, setQuickOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);

  const canCreateMatter = hasAccess('matter', 'create');

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canCreateMatter && (
        <Button
          size="sm"
          className="gap-1.5 bg-[#0069D1] hover:bg-[#0058b0]"
          onClick={() => navigate('/app/matters/new')}
        >
          <Plus className="h-4 w-4" />
          {t('dashboard.case360.newMatter')}
        </Button>
      )}
      {canCreateMatter && (
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
          onClick={() => setQuickOpen(true)}
        >
          <Zap className="h-4 w-4" />
          {t('dashboard.case360.quickAdvice')}
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        className="gap-1.5"
        onClick={() => navigate('/app/calendar')}
      >
        <CalendarPlus className="h-4 w-4" />
        {t('dashboard.case360.addEvent')}
      </Button>
      {showSearch && (
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => setSearchOpen(true)}
        >
          <Search className="h-4 w-4" />
          {t('dashboard.case360.search.button')}
        </Button>
      )}

      {canCreateMatter && (
        <QuickAdviceDialog open={quickOpen} onOpenChange={setQuickOpen} />
      )}
      {showSearch && (
        <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      )}
    </div>
  );
}
